import { useMemo, useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { useAsync } from '../../api/useAsync';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { can, mayRollOutVersion } from '../../auth/capabilities';
import { TENANT_STATES } from '../../api/types';
import type { Tenant, TenantState } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Link, useRouter } from '../../router/Router';
import { Ago } from '../../ui/Ago';
import { DataTable } from '../../ui/DataTable';
import type { Column } from '../../ui/DataTable';
import { Button, Card, Loading, PageHead, Stat } from '../../ui/primitives';
import { FilterBar, SearchInput, SegmentedFilter } from '../../ui/form';
import { ErrorAlert } from '../../ui/Alert';
import { StatusBadge, TenantStateBadge, tenantStateLabelKey } from '../../ui/StatusBadge';
import { IconPlus } from '../../ui/icons';
import { CreateTenantDialog } from './CreateTenantDialog';
import { RolloutLine } from './RolloutLine';
import { movingNow } from './rollout';

type Filter = TenantState | 'all';

/** Stable identity so the memo below is not invalidated every render. */
const NO_TENANTS: Tenant[] = [];

/**
 * Every tenancy this account may see, and the state of the fleet above them.
 *
 * There is no separate overview screen and this is why: the control plane has
 * no aggregating route, so one would be assembled from this same list and would
 * be a second view of it. Instead the list carries what an overview would have
 * said — four figures, and whether anything is being moved across the fleet
 * right now.
 *
 * `up_to_date` is a column and not a detail on the card: it says which clusters
 * have been told about what was declared for them, which is what somebody
 * scanning this table is scanning for.
 */
export function TenantsScreen() {
  const { t, formatNumber } = useI18n();
  const api = useAccountApi();
  const { session } = useAccountSession();
  const { navigate } = useRouter();

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const state = useAsync((signal) => api.provider.listTenants(signal), 'tenants');
  const board = useAsync((signal) => api.provider.readRollout(signal), 'rollout');
  const tenants: Tenant[] = state.status === 'ready' ? state.value : NO_TENANTS;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tenants.filter((tenant) => {
      if (filter !== 'all' && tenant.state !== filter) return false;
      if (needle === '') return true;
      return `${tenant.display_name} ${tenant.slug} ${tenant.tenancy_domain}`
        .toLowerCase()
        .includes(needle);
    });
  }, [tenants, filter, query]);

  const ready = tenants.filter((tenant) => tenant.state === 'ready').length;
  const behind = tenants.filter((tenant) => !tenant.up_to_date).length;
  const refused = tenants.filter((tenant) => tenant.state === 'failed').length;

  const columns: Column<Tenant>[] = [
    {
      key: 'name',
      header: t('tenants.col.name'),
      sort: (a, b) => a.display_name.localeCompare(b.display_name),
      cell: (tenant) => (
        <span className="cell-flex">
          <Link to={{ name: 'account.tenant', slug: tenant.slug }} className="row-link">
            <span className="t-name">{tenant.display_name}</span>
          </Link>
          <span className="t-sub mono">{tenant.slug}</span>
        </span>
      ),
    },
    {
      key: 'domain',
      header: t('tenants.col.domain'),
      mono: true,
      sort: (a, b) => a.tenancy_domain.localeCompare(b.tenancy_domain),
      cell: (tenant) => tenant.tenancy_domain,
    },
    {
      key: 'state',
      header: t('tenants.col.state'),
      sort: (a, b) => a.state.localeCompare(b.state),
      cell: (tenant) => <TenantStateBadge state={tenant.state} />,
    },
    {
      key: 'declared',
      header: t('tenants.col.declared'),
      sort: (a, b) => Number(a.up_to_date) - Number(b.up_to_date),
      cell: (tenant) => <DeclaredCell tenant={tenant} />,
    },
    {
      key: 'version',
      header: t('tenants.col.version'),
      mono: true,
      hideSm: true,
      sort: (a, b) => a.version.localeCompare(b.version),
      cell: (tenant) => tenant.version,
    },
    {
      key: 'created',
      header: t('tenants.col.created'),
      hideSm: true,
      sort: (a, b) => a.created_at.localeCompare(b.created_at),
      cell: (tenant) => <Ago at={tenant.created_at} />,
    },
  ];

  return (
    <div className="content-inner">
      <PageHead
        title={t('tenants.title')}
        {...(can(session, 'create_tenant')
          ? {
              actions: (
                <Button
                  variant="primary"
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <IconPlus size={16} />
                  {t('tenants.create')}
                </Button>
              ),
            }
          : {})}
      />

      {state.status === 'error' && (
        <ErrorAlert
          error={state.error}
          action={
            <div style={{ marginTop: 12 }}>
              <Button size="sm" onClick={state.reload}>
                {t('common.retry')}
              </Button>
            </div>
          }
        />
      )}

      {state.status === 'loading' && <Loading />}

      {state.status === 'ready' && (
        <>
          <div className="grid g-4" style={{ marginBottom: 'var(--gap)' }}>
            <Stat label={t('tenants.summary.total')} value={formatNumber(tenants.length)} />
            <Stat label={t('tenants.summary.ready')} value={formatNumber(ready)} />
            <Stat
              label={t('tenants.summary.behind')}
              value={formatNumber(behind)}
              {...(behind > 0 ? { tone: 'warn' as const } : {})}
            />
            <Stat
              label={t('tenants.summary.failed')}
              value={formatNumber(refused)}
              {...(refused > 0 ? { tone: 'deny' as const } : {})}
            />
          </div>

          {/* Whether anything is moving across the fleet, where somebody is
              standing when it matters. The way to the board is on it only for
              the account that has one: the desk reads a tenancy's version off
              that tenancy's card. */}
          {board.status === 'ready' && (
            <div style={{ marginBottom: 'var(--gap)' }}>
              <RolloutLine
                rollout={board.value.rollout}
                moving={movingNow(board.value)}
                {...(mayRollOutVersion(session)
                  ? {
                      link: (
                        <Link to={{ name: 'account.rollout' }} className="text-action">
                          {t('nav.account.rollout')}
                        </Link>
                      ),
                    }
                  : {})}
              />
            </div>
          )}

          <FilterBar
            filters={
              <SegmentedFilter<Filter>
                legend={t('tenants.filter.label')}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: t('tenants.filter.all'), count: tenants.length },
                  ...TENANT_STATES.map((value) => ({
                    value,
                    label: t(tenantStateLabelKey(value)),
                    count: tenants.filter((tenant) => tenant.state === value).length,
                  })),
                ]}
              />
            }
            search={
              <SearchInput
                label={t('tenants.search.label')}
                placeholder={t('tenants.search.placeholder')}
                value={query}
                onChange={setQuery}
              />
            }
          />

          <Card>
            <DataTable<Tenant>
              caption={t('tenants.title')}
              columns={columns}
              rows={visible}
              total={tenants.length}
              rowKey={(tenant) => tenant.slug}
              initialSort={{ key: 'name', direction: 'asc' }}
              onRowClick={(tenant) => {
                navigate({ name: 'account.tenant', slug: tenant.slug });
              }}
              empty={{
                title: t('tenants.none.title'),
                body: t('tenants.none.body'),
                // A fleet with nothing in it has exactly one thing to do next,
                // for the account that may do it.
                ...(can(session, 'create_tenant')
                  ? {
                      action: (
                        <Button
                          variant="primary"
                          onClick={() => {
                            setCreating(true);
                          }}
                        >
                          <IconPlus size={16} />
                          {t('tenants.create')}
                        </Button>
                      ),
                    }
                  : {}),
              }}
              noMatch={{ title: t('tenants.empty.title'), body: t('tenants.empty.body') }}
            />
          </Card>
        </>
      )}

      {creating && (
        <CreateTenantDialog
          onClose={() => {
            setCreating(false);
          }}
          onCreated={() => {
            setCreating(false);
            state.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Whether the cluster has been told about what is declared for it, as the two
 * numbers it is.
 *
 * The catalogue and the rest of a tenancy's desired state are declared here and
 * reach the cluster on the next application, so a tenancy can be perfectly
 * healthy and still be running last week's declaration. A column of sentences
 * saying so is not a column somebody scans; two numbers and a mark are.
 */
function DeclaredCell({ tenant }: { tenant: Tenant }) {
  const { t, formatNumber } = useI18n();

  if (tenant.up_to_date) {
    return <StatusBadge tone="ok" label={t('tenants.declared.told')} />;
  }
  return (
    <span className="mono t-warn">
      {formatNumber(tenant.generation)}
      {' / '}
      {tenant.applied_generation === null
        ? t('tenants.declared.never')
        : formatNumber(tenant.applied_generation)}
    </span>
  );
}
